import { FC, useState } from 'react';

import { Checkbox, Drawer, FormControlLabel, Stack, TextField } from '@mui/material';

import { CreateMcpEndpointRequest } from '@/apis/admin-api';
import { PrimaryButton, SecondaryButton } from '@/components/Shared/Buttons';

import { ALL_LIBRARY_SCOPES, fieldSx, MCP_SERVER_NAME_PATTERN, SDK_DOCS_SCOPE } from '../constants';
import { LibraryScopePicker } from './LibraryScopePicker';

import styles from '../styles.module.scss';

type CreateServerSidepanelProps = {
  urlPrefix: string;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (request: CreateMcpEndpointRequest) => void;
};

export const CreateServerSidepanel: FC<CreateServerSidepanelProps> = ({
  urlPrefix,
  isSubmitting,
  onCancel,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [libraryScopes, setLibraryScopes] = useState<string[]>(ALL_LIBRARY_SCOPES);
  const [shared, setShared] = useState(true);

  const handleSubmit = () => {
    if (isSubmitting) return;
    if (!MCP_SERVER_NAME_PATTERN.test(name)) {
      setError('Use 1–64 lowercase letters, numbers and hyphens, starting and ending with a letter or number');
      return;
    }
    onSubmit({
      name,
      scopes: [SDK_DOCS_SCOPE, ...ALL_LIBRARY_SCOPES.filter((scope) => libraryScopes.includes(scope))],
      shared,
    });
  };

  return (
    <Drawer open anchor="right" onClose={onCancel}>
      <div className={styles['sidepanel']}>
        <Stack flex="auto 1 1" spacing="20px" overflow="auto">
          <p style={{ fontWeight: '600', margin: '8px 0 12px' }}>New MCP server</p>
          <TextField
            required
            label="Name"
            placeholder="e.g. materials"
            value={name}
            onChange={(e) => {
              setName(e.target.value.toLowerCase());
              setError(undefined);
            }}
            error={!!error}
            helperText={error ?? `${urlPrefix}${name || '<name>'}`}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
            sx={fieldSx}
          />
          <LibraryScopePicker value={libraryScopes} onChange={setLibraryScopes} />
          <FormControlLabel
            control={<Checkbox checked={shared} onChange={(e) => setShared(e.target.checked)} />}
            label="Share with everyone in the organization"
          />
          <p className={styles['muted']}>
            {shared
              ? 'Everyone in your organization can use this server.'
              : 'Only organization admins can see and use this server.'}{' '}
            Each person signs in with their own Treble account or creates an API key for it. Deleting the server revokes
            its API keys.
          </p>
        </Stack>
        <div style={{ height: 40, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SecondaryButton width={150} label="Cancel" onClick={onCancel} />
          <PrimaryButton width={150} label="Create server" disabled={isSubmitting} onClick={handleSubmit} />
        </div>
      </div>
    </Drawer>
  );
};
